import React, { useState } from 'react';
import { AlertCircle, FileText, Truck, Wallet, ListOrdered } from 'lucide-react';
import { PurchaseOrder, PurchaseOrderHeaderPayload } from '../../../../../types/PurchaseOrder.types';
import { Supplier } from '../../../../../types/Supplier.types';
import { Society } from '../../../../../types/Society.types';
import { Warehouse } from '../../../../../types/Warehouse.types';
import { CostCenter } from '../../../../../types/CostCenter.types';
import { Book } from '../../../../../types/Book.types';
import { SidebarForm } from '../../../../../components/shared/SidebarForm/SidebarForm';
import { DetailSection } from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { DEFAULT_CURRENCY, INCOTERM_OPTIONS } from '../../../../../constants/purchases';
import {
  LineDraft,
  PurchaseOrderLinesEditor,
  emptyLineDraft,
  lineDraftFromLine,
  validateLineDrafts
} from './PurchaseOrderLinesEditor';
import { parseNumber } from './format';

interface HeaderFormData {
  supplierId: string;
  companyId: string;
  warehouseId: string;
  costCenterId: string;
  expectedDeliveryDate: string;
  alternateDeliveryAddress: string;
  incoterm: string;
  shippingMethod: string;
  carrier: string;
  currency: string;
  exchangeRate: string;
  notes: string;
}

const EMPTY_HEADER: HeaderFormData = {
  supplierId: '',
  companyId: '',
  warehouseId: '',
  costCenterId: '',
  expectedDeliveryDate: '',
  alternateDeliveryAddress: '',
  incoterm: '',
  shippingMethod: '',
  carrier: '',
  currency: DEFAULT_CURRENCY,
  exchangeRate: '',
  notes: ''
};

const headerFromOrder = (order: PurchaseOrder): HeaderFormData => ({
  supplierId: order.supplierId,
  companyId: order.companyId,
  warehouseId: order.warehouseId,
  costCenterId: order.costCenterId || '',
  expectedDeliveryDate: order.expectedDeliveryDate || '',
  alternateDeliveryAddress: order.alternateDeliveryAddress,
  incoterm: order.incoterm,
  shippingMethod: order.shippingMethod,
  carrier: order.carrier,
  currency: order.currency || DEFAULT_CURRENCY,
  exchangeRate: order.exchangeRate === null ? '' : String(order.exchangeRate),
  notes: order.notes
});

interface PurchaseOrderFormProps {
  mode: 'create' | 'edit';
  initial?: PurchaseOrder;
  suppliers: Supplier[];
  companies: Society[];
  warehouses: Warehouse[];
  costCenters: CostCenter[];
  books: Book[];
  isSaving: boolean;
  error: string;
  onSubmit: (header: PurchaseOrderHeaderPayload, lines: LineDraft[]) => void;
  onCancel: () => void;
  onValidationError: (message: string) => void;
}

// Alta y edición de borradores comparten formulario. El contenedor decide qué
// llamadas hacer con lo que sale de aquí (create + lines_add, o update + diff de líneas).
export const PurchaseOrderForm = ({
  mode,
  initial,
  suppliers,
  companies,
  warehouses,
  costCenters,
  books,
  isSaving,
  error,
  onSubmit,
  onCancel,
  onValidationError
}: PurchaseOrderFormProps) => {
  const [header, setHeader] = useState<HeaderFormData>(initial ? headerFromOrder(initial) : EMPTY_HEADER);
  const [lines, setLines] = useState<LineDraft[]>(
    initial && initial.lines.length > 0 ? initial.lines.map(lineDraftFromLine) : [emptyLineDraft()]
  );

  const setField = (field: keyof HeaderFormData, value: string) =>
    setHeader(prev => ({ ...prev, [field]: value }));

  // El backend exige almacén físico. Un almacén pertenece a una sociedad, así
  // que con sociedad elegida se ofrecen primero los suyos; si no tiene ninguno
  // físico se muestran todos, porque el backend no cruza sociedad y almacén.
  const allPhysical = warehouses.filter(w => w.type === 'physical');
  const companyPhysical = allPhysical.filter(w => !header.companyId || !w.societyId || w.societyId === header.companyId);
  const physicalWarehouses = companyPhysical.length > 0 ? companyPhysical : allPhysical;
  const companyCostCenters = costCenters.filter(c =>
    c.status === 'active' && (!header.companyId || c.companyId === header.companyId)
  );
  const activeSuppliers = suppliers.filter(s => s.status === 'active' || s.id === header.supplierId);

  const handleSubmit = () => {
    if (!header.supplierId || !header.companyId || !header.warehouseId) {
      onValidationError('Acreedor, sociedad y almacén de destino son obligatorios.');
      return;
    }
    const lineError = validateLineDrafts(lines);
    if (lineError) {
      onValidationError(lineError);
      return;
    }

    onSubmit({
      supplierId: header.supplierId,
      companyId: header.companyId,
      warehouseId: header.warehouseId,
      costCenterId: header.costCenterId || undefined,
      expectedDeliveryDate: header.expectedDeliveryDate || undefined,
      alternateDeliveryAddress: header.alternateDeliveryAddress.trim() || undefined,
      incoterm: header.incoterm || undefined,
      shippingMethod: header.shippingMethod.trim() || undefined,
      carrier: header.carrier.trim() || undefined,
      currency: header.currency.trim().toUpperCase() || DEFAULT_CURRENCY,
      exchangeRate: header.exchangeRate.trim() === '' ? undefined : parseNumber(header.exchangeRate),
      notes: header.notes.trim() || undefined
    }, lines);
  };

  return (
    <SidebarForm
      drawer
      title={mode === 'edit' ? `Modificar_Orden ${initial ? initial.poNumber : ''}` : 'Nueva_Orden_De_Compra'}
      onClose={onCancel}
      submitLabel={isSaving ? 'Guardando...' : mode === 'edit' ? 'Guardar_Cambios' : 'Crear_Borrador'}
      submitDisabled={isSaving}
      onSubmit={handleSubmit}
    >
      {error && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{error}</span>
        </div>
      )}

      <DetailSection icon={FileText} title="1. Datos_Generales">
        <div className="sidebar-form-grid-2">
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Acreedor *</label>
            <select
              value={header.supplierId}
              onChange={(e) => setField('supplierId', e.target.value)}
              className="sidebar-form-input"
            >
              <option value="">— Seleccione acreedor —</option>
              {activeSuppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Sociedad compradora *</label>
            <select
              value={header.companyId}
              onChange={(e) => {
                const val = e.target.value;
                // Al cambiar de sociedad, almacén y centro de coste dejan de ser válidos.
                setHeader(prev => ({ ...prev, companyId: val, warehouseId: '', costCenterId: '' }));
              }}
              className="sidebar-form-input"
            >
              <option value="">— Seleccione sociedad —</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Almacén de destino *</label>
            <select
              value={header.warehouseId}
              onChange={(e) => setField('warehouseId', e.target.value)}
              className="sidebar-form-input"
            >
              <option value="">— Seleccione almacén físico —</option>
              {physicalWarehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
        </div>
        <div className="sidebar-form-grid-2">
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Centro de coste</label>
            <select
              value={header.costCenterId}
              onChange={(e) => setField('costCenterId', e.target.value)}
              className="sidebar-form-input"
            >
              <option value="">— Sin centro de coste —</option>
              {companyCostCenters.map(c => <option key={c.id} value={c.id}>{c.code ? `${c.code} · ` : ''}{c.name}</option>)}
            </select>
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Moneda</label>
            <input
              type="text"
              maxLength={3}
              value={header.currency}
              onChange={(e) => setField('currency', e.target.value.toUpperCase())}
              className="sidebar-form-input"
            />
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Tipo de cambio</label>
            <input
              type="number"
              min="0"
              step="0.0001"
              value={header.exchangeRate}
              onChange={(e) => setField('exchangeRate', e.target.value)}
              placeholder="Solo si la moneda no es EUR"
              className="sidebar-form-input"
            />
          </div>
        </div>
      </DetailSection>

      <DetailSection icon={Truck} title="2. Entrega_Y_Transporte">
        <div className="sidebar-form-grid-2">
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Fecha de entrega prevista</label>
            <input
              type="date"
              value={header.expectedDeliveryDate}
              onChange={(e) => setField('expectedDeliveryDate', e.target.value)}
              className="sidebar-form-input"
            />
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Incoterm</label>
            <select
              value={header.incoterm}
              onChange={(e) => setField('incoterm', e.target.value)}
              className="sidebar-form-input"
            >
              <option value="">— Sin incoterm —</option>
              {INCOTERM_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Método de envío</label>
            <input
              type="text"
              value={header.shippingMethod}
              onChange={(e) => setField('shippingMethod', e.target.value)}
              placeholder="Terrestre, marítimo, aéreo..."
              className="sidebar-form-input"
            />
          </div>
        </div>
        <div className="sidebar-form-grid-2">
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Transportista</label>
            <input
              type="text"
              value={header.carrier}
              onChange={(e) => setField('carrier', e.target.value)}
              className="sidebar-form-input"
            />
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Dirección de entrega alternativa</label>
            <input
              type="text"
              value={header.alternateDeliveryAddress}
              onChange={(e) => setField('alternateDeliveryAddress', e.target.value)}
              placeholder="Solo si no se entrega en el almacén"
              className="sidebar-form-input"
            />
          </div>
        </div>
      </DetailSection>

      <DetailSection icon={ListOrdered} title="3. Líneas_De_Compra">
        <PurchaseOrderLinesEditor
          lines={lines}
          books={books}
          currency={header.currency || DEFAULT_CURRENCY}
          onChange={setLines}
          disabled={isSaving}
        />
      </DetailSection>

      <DetailSection icon={Wallet} title="4. Observaciones">
        <div className="sidebar-form-group">
          <textarea
            rows={3}
            value={header.notes}
            onChange={(e) => setField('notes', e.target.value)}
            placeholder="Notas internas de la orden"
            className="sidebar-form-input"
          />
        </div>
      </DetailSection>
    </SidebarForm>
  );
};
